// Brain memory layer — Firebase Realtime DB reads/writes for /brain/*.
//
// Browser-side. Worker doesn't touch Firebase directly — the SDK loads
// thread history client-side and passes it to the Worker so Claude has context.
// Cleaner separation, and the Worker doesn't need Firebase Admin SDK.
//
// See spec/10-module-brain.md + spec/20-data-model.md for the canonical
// data model. Schema enforced by convention here; Firebase Security Rules
// (firebase.rules.json) enforce auth.

import {
  ref,
  push,
  set,
  get,
  query,
  orderByChild,
  limitToLast,
  equalTo,
  type DatabaseReference,
} from 'firebase/database';
import { db, awaitFirebaseUser } from './firebase';

// =====================================================
// Types (mirror spec/20-data-model.md)
// =====================================================

export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';

export interface BrainMessage {
  id: string;
  role: MessageRole;
  content: string;
  ts: string;
  userId?: string;
  contextRef?: { module: string; entityType: string; entityId: string };
  tokensIn?: number;
  tokensOut?: number;
  model?: string;
  cost?: number;
}

export interface BrainThread {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string; // derived from the first user message
  messageCount: number;
  lastUserMessagePreview: string;
}

export interface BrainFact {
  id: string;
  category: string;
  key: string;
  value: unknown;
  confidence: number;
  source: 'user_told' | 'extracted_from_doc' | 'inferred_from_pattern';
  sourceRef?: { docId?: string; threadId?: string; messageId?: string };
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
}

// =====================================================
// Threads + messages
// =====================================================

function threadsRef(): DatabaseReference {
  return ref(db, 'brain/threads');
}
function threadRef(threadId: string): DatabaseReference {
  return ref(db, `brain/threads/${threadId}`);
}
function messagesRef(threadId: string): DatabaseReference {
  return ref(db, `brain/messages/${threadId}`);
}

/**
 * Create a new thread. Returns the new threadId. The first user message
 * (firstMessage) is used as the title (truncated to 80 chars).
 */
export async function createThread(firstMessage: string): Promise<string> {
  await awaitFirebaseUser();
  const newRef = push(threadsRef());
  const id = newRef.key!;
  const now = new Date().toISOString();
  const title = firstMessage.trim().slice(0, 80) || '(empty)';
  const thread: Omit<BrainThread, 'id'> = {
    createdAt: now,
    updatedAt: now,
    title,
    messageCount: 0,
    lastUserMessagePreview: title,
  };
  await set(newRef, thread);
  return id;
}

/**
 * Strip undefined-valued keys from a plain object. Firebase RTDB rejects
 * writes that contain `undefined` anywhere in the payload.
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/**
 * Append a message to a thread. Returns the new message ID.
 * Also updates the thread's `updatedAt` and `messageCount`.
 */
export async function appendMessage(
  threadId: string,
  msg: Omit<BrainMessage, 'id' | 'ts'>,
): Promise<string> {
  await awaitFirebaseUser();
  const newRef = push(messagesRef(threadId));
  const id = newRef.key!;
  const ts = new Date().toISOString();
  // Strip undefined values — Firebase RTDB rejects them
  const payload = { ...stripUndefined(msg as Record<string, unknown>), ts };
  await set(newRef, payload);

  // Update thread aggregate fields (read-modify-write — fine for single-user dev volume)
  const tSnap = await get(threadRef(threadId));
  if (tSnap.exists()) {
    const t = tSnap.val() as Omit<BrainThread, 'id'>;
    const updates: Partial<BrainThread> = {
      updatedAt: ts,
      messageCount: (t.messageCount ?? 0) + 1,
    };
    if (msg.role === 'user') {
      updates.lastUserMessagePreview = msg.content.slice(0, 200);
    }
    await set(threadRef(threadId), { ...t, ...updates });
  }

  return id;
}

/**
 * Load all messages on a thread, oldest first.
 */
export async function loadMessages(threadId: string): Promise<BrainMessage[]> {
  await awaitFirebaseUser();
  const snap = await get(query(messagesRef(threadId), orderByChild('ts')));
  if (!snap.exists()) return [];
  const out: BrainMessage[] = [];
  snap.forEach((child) => {
    out.push({ id: child.key!, ...(child.val() as Omit<BrainMessage, 'id'>) });
  });
  return out;
}

/**
 * Load a single thread record.
 */
export async function loadThread(threadId: string): Promise<BrainThread | null> {
  await awaitFirebaseUser();
  const snap = await get(threadRef(threadId));
  if (!snap.exists()) return null;
  return { id: threadId, ...(snap.val() as Omit<BrainThread, 'id'>) };
}

/**
 * List the most recent N threads, newest first.
 */
export async function listRecentThreads(limit = 20): Promise<BrainThread[]> {
  await awaitFirebaseUser();
  const snap = await get(query(threadsRef(), orderByChild('updatedAt'), limitToLast(limit)));
  if (!snap.exists()) return [];
  const out: BrainThread[] = [];
  snap.forEach((child) => {
    out.push({ id: child.key!, ...(child.val() as Omit<BrainThread, 'id'>) });
  });
  return out.reverse(); // newest first
}

/**
 * Cross-session context: pull the last N messages from all prior threads
 * EXCLUDING the current one, in chronological order. Injected as preamble
 * on the first turn of a new thread so Jemma carries context across sessions.
 * Mirrors jemma-os's brain.py:_inject_prior_context pattern.
 */
export async function loadCrossSessionContext(
  excludeThreadId: string | null,
  limit = 8,
): Promise<BrainMessage[]> {
  await awaitFirebaseUser();
  // Get recent threads (up to 5), pull last few messages from each, then trim
  const recentThreads = await listRecentThreads(5);
  const all: BrainMessage[] = [];
  for (const t of recentThreads) {
    if (t.id === excludeThreadId) continue;
    const msgs = await loadMessages(t.id);
    all.push(...msgs.slice(-4));
  }
  // Sort by ts ascending, take last `limit`
  all.sort((a, b) => a.ts.localeCompare(b.ts));
  return all.slice(-limit);
}

// =====================================================
// Facts
// =====================================================

function factsRef(): DatabaseReference {
  return ref(db, 'brain/facts');
}

export interface RememberInput {
  category: string;
  key: string;
  value: unknown;
  confidence?: number;
  source: 'user_told' | 'extracted_from_doc' | 'inferred_from_pattern';
  sourceRef?: { docId?: string; threadId?: string; messageId?: string };
}

/**
 * Write or update a fact. Facts are keyed by (category, key) — writing the
 * same (category, key) again UPDATES rather than duplicates.
 */
export async function rememberFact(input: RememberInput): Promise<string> {
  await awaitFirebaseUser();
  // Look up existing fact by category+key
  const existing = await findFactByKey(input.category, input.key);
  const now = new Date().toISOString();

  if (existing) {
    const updated: BrainFact = {
      ...existing,
      value: input.value,
      confidence: input.confidence ?? existing.confidence,
      source: input.source,
      sourceRef: input.sourceRef ?? existing.sourceRef,
      updatedAt: now,
    };
    await set(ref(db, `brain/facts/${existing.id}`), {
      category: updated.category,
      key: updated.key,
      value: updated.value,
      confidence: updated.confidence,
      source: updated.source,
      sourceRef: updated.sourceRef ?? null,
      createdAt: updated.createdAt,
      updatedAt: updated.updatedAt,
    });
    return existing.id;
  }

  const newRef = push(factsRef());
  const id = newRef.key!;
  const fact: Omit<BrainFact, 'id'> = {
    category: input.category,
    key: input.key,
    value: input.value,
    confidence: input.confidence ?? 1,
    source: input.source,
    sourceRef: input.sourceRef,
    createdAt: now,
    updatedAt: now,
  };
  // Strip undefined values — Firebase rejects them
  await set(newRef, stripUndefined(fact as Record<string, unknown>));
  return id;
}

export interface RecallQuery {
  category?: string;
  key?: string;
  keyPrefix?: string;
  limit?: number;
}

/**
 * Recall facts. Supports filter by category, exact key, or key prefix.
 */
export async function recallFacts(q: RecallQuery): Promise<BrainFact[]> {
  await awaitFirebaseUser();
  let snap;
  if (q.key && q.category) {
    // Most specific — single fact lookup
    const fact = await findFactByKey(q.category, q.key);
    return fact ? [fact] : [];
  } else if (q.category) {
    snap = await get(query(factsRef(), orderByChild('category'), equalTo(q.category)));
  } else {
    snap = await get(factsRef());
  }

  if (!snap || !snap.exists()) return [];

  const out: BrainFact[] = [];
  snap.forEach((child) => {
    out.push({ id: child.key!, ...(child.val() as Omit<BrainFact, 'id'>) });
  });

  let filtered = out;
  if (q.keyPrefix) {
    filtered = filtered.filter((f) => f.key.startsWith(q.keyPrefix!));
  }
  if (q.limit) filtered = filtered.slice(0, q.limit);
  return filtered;
}

async function findFactByKey(category: string, key: string): Promise<BrainFact | null> {
  const snap = await get(query(factsRef(), orderByChild('category'), equalTo(category)));
  if (!snap.exists()) return null;
  let found: BrainFact | null = null;
  snap.forEach((child) => {
    const f = child.val() as Omit<BrainFact, 'id'>;
    if (f.key === key) {
      found = { id: child.key!, ...f };
      return true; // stop iteration
    }
    return false;
  });
  return found;
}

// =====================================================
// Reminders (M1.4) — /brain/reminders
// =====================================================

export type ReminderStatus = 'pending' | 'done' | 'snoozed' | 'cancelled';

export interface BrainReminder {
  id: string;
  text: string;
  dueAt: string; // ISO 8601
  rrule?: string; // RFC 5545 — not parsed yet, stored for future
  status: ReminderStatus;
  snoozedUntil?: string;
  createdAt: string;
  createdInThreadId?: string;
  relatedEntity?: { module: string; entityType: string; entityId: string };
  notifiedAt?: string[];
  completedAt?: string;
  completedNotes?: string;
}

function remindersRef(): DatabaseReference {
  return ref(db, 'brain/reminders');
}

export interface CreateReminderInput {
  text: string;
  dueAt: string;
  rrule?: string;
  relatedEntity?: { module: string; entityType: string; entityId: string };
  createdInThreadId?: string;
}

export async function createReminder(input: CreateReminderInput): Promise<string> {
  await awaitFirebaseUser();
  const newRef = push(remindersRef());
  const id = newRef.key!;
  const now = new Date().toISOString();
  const reminder: Omit<BrainReminder, 'id'> = {
    text: input.text,
    dueAt: input.dueAt,
    rrule: input.rrule,
    status: 'pending',
    createdAt: now,
    createdInThreadId: input.createdInThreadId,
    relatedEntity: input.relatedEntity,
  };
  await set(newRef, stripUndefined(reminder as Record<string, unknown>));
  return id;
}

export async function listReminders(
  status: ReminderStatus | 'all' = 'pending',
): Promise<BrainReminder[]> {
  await awaitFirebaseUser();
  const snap = await get(query(remindersRef(), orderByChild('dueAt')));
  if (!snap.exists()) return [];
  const out: BrainReminder[] = [];
  snap.forEach((child) => {
    const r = { id: child.key!, ...(child.val() as Omit<BrainReminder, 'id'>) };
    if (status === 'all' || r.status === status) out.push(r);
  });
  return out;
}

export async function updateReminderStatus(
  id: string,
  status: ReminderStatus,
  extra?: { completedNotes?: string; snoozedUntil?: string },
): Promise<void> {
  await awaitFirebaseUser();
  const rRef = ref(db, `brain/reminders/${id}`);
  const snap = await get(rRef);
  if (!snap.exists()) throw new Error(`Reminder ${id} not found`);
  const existing = snap.val() as Omit<BrainReminder, 'id'>;
  const updates: Record<string, unknown> = { ...existing, status };
  if (status === 'done') updates.completedAt = new Date().toISOString();
  if (extra?.completedNotes) updates.completedNotes = extra.completedNotes;
  if (extra?.snoozedUntil) updates.snoozedUntil = extra.snoozedUntil;
  await set(rRef, stripUndefined(updates));
}

// =====================================================
// Flags (M1.6) — /brain/flags
// =====================================================

export type FlagSeverity = 'info' | 'attention' | 'urgent';
export type FlagStatus = 'pending' | 'acknowledged' | 'dismissed' | 'snoozed';

export interface BrainFlag {
  id: string;
  severity: FlagSeverity;
  category: string;
  title: string;
  body: string;
  suggestedAction?: string;
  relatedEntity?: { module: string; entityType: string; entityId: string };
  sourceWatcherId?: string;
  createdAt: string;
  status: FlagStatus;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  dismissedAt?: string;
  dismissedBy?: string;
  dismissedReason?: string;
  snoozedUntil?: string;
}

function flagsRef(): DatabaseReference {
  return ref(db, 'brain/flags');
}

export interface RaiseFlagInput {
  severity: FlagSeverity;
  category: string;
  title: string;
  body: string;
  suggestedAction?: string;
  relatedEntity?: { module: string; entityType: string; entityId: string };
  sourceWatcherId?: string;
}

/**
 * Raise a flag. If a flag with the same (category, sourceWatcherId, relatedEntity)
 * is already pending, returns the existing one's ID without creating a duplicate.
 * (Per spec/10-module-brain.md: "Watchers don't notify on every run — only on state change.")
 */
export async function raiseFlag(input: RaiseFlagInput): Promise<string> {
  await awaitFirebaseUser();
  // Dedup: skip if equivalent flag is already pending
  const existing = await listFlags({ status: 'pending' });
  const sameRefEntityId = input.relatedEntity?.entityId ?? '';
  const dupe = existing.find(
    (f) =>
      f.category === input.category &&
      (f.sourceWatcherId ?? '') === (input.sourceWatcherId ?? '') &&
      (f.relatedEntity?.entityId ?? '') === sameRefEntityId,
  );
  if (dupe) return dupe.id;

  const newRef = push(flagsRef());
  const id = newRef.key!;
  const flag: Omit<BrainFlag, 'id'> = {
    severity: input.severity,
    category: input.category,
    title: input.title,
    body: input.body,
    suggestedAction: input.suggestedAction,
    relatedEntity: input.relatedEntity,
    sourceWatcherId: input.sourceWatcherId,
    createdAt: new Date().toISOString(),
    status: 'pending',
  };
  await set(newRef, stripUndefined(flag as Record<string, unknown>));
  return id;
}

export interface ListFlagsFilter {
  status?: FlagStatus | 'all';
  severity?: FlagSeverity;
  category?: string;
  limit?: number;
}

export async function listFlags(filter: ListFlagsFilter = {}): Promise<BrainFlag[]> {
  await awaitFirebaseUser();
  const snap = await get(query(flagsRef(), orderByChild('createdAt')));
  if (!snap.exists()) return [];
  const out: BrainFlag[] = [];
  snap.forEach((child) => {
    out.push({ id: child.key!, ...(child.val() as Omit<BrainFlag, 'id'>) });
  });
  // Filter + sort (newest first)
  let filtered = out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const statusFilter = filter.status ?? 'pending';
  if (statusFilter !== 'all') {
    filtered = filtered.filter((f) => f.status === statusFilter);
  }
  if (filter.severity) filtered = filtered.filter((f) => f.severity === filter.severity);
  if (filter.category) filtered = filtered.filter((f) => f.category === filter.category);
  if (filter.limit) filtered = filtered.slice(0, filter.limit);
  return filtered;
}

export async function updateFlagStatus(
  id: string,
  status: FlagStatus,
  extra?: { user?: string; reason?: string; snoozedUntil?: string },
): Promise<void> {
  await awaitFirebaseUser();
  const fRef = ref(db, `brain/flags/${id}`);
  const snap = await get(fRef);
  if (!snap.exists()) throw new Error(`Flag ${id} not found`);
  const existing = snap.val() as Omit<BrainFlag, 'id'>;
  const now = new Date().toISOString();
  const updates: Record<string, unknown> = { ...existing, status };
  if (status === 'acknowledged') {
    updates.acknowledgedAt = now;
    updates.acknowledgedBy = extra?.user;
  } else if (status === 'dismissed') {
    updates.dismissedAt = now;
    updates.dismissedBy = extra?.user;
    updates.dismissedReason = extra?.reason;
  } else if (status === 'snoozed') {
    updates.snoozedUntil = extra?.snoozedUntil;
  }
  await set(fRef, stripUndefined(updates));
}

// =====================================================
// Helpers — used by Brain SDK
// =====================================================

/** Convert BrainMessages → Worker-shaped history array. */
export function messagesToHistory(messages: BrainMessage[]): Array<{ role: string; content: string }> {
  return messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({ role: m.role, content: m.content }));
}
